import React, { useRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';

/**
 * DetectionImageCanvas - Displays capture image with bounding boxes for all detected faces
 *
 * @param {string} imageUrl - Full HTTPS URL of the capture image
 * @param {Array} detectedFaces - Array of face objects with boundingBox and match info
 * @param {Function} onFaceClick - Optional callback when face box is clicked
 */
export default function DetectionImageCanvas({ imageUrl, detectedFaces = [], onFaceClick }) {
  const containerRef = useRef(null);
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });
  const [hoveredFaceIndex, setHoveredFaceIndex] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('[DetectionImageCanvas] Received data:', {
      imageUrl,
      detectedFacesCount: detectedFaces?.length,
      firstFace: detectedFaces?.[0],
      allFaces: detectedFaces
    });
  }, [imageUrl, detectedFaces]);

  // Load image and get natural dimensions
  useEffect(() => {
    if (!imageUrl) return;

    const img = new Image();
    img.onload = () => {
      // Get container width
      const containerWidth = containerRef.current?.offsetWidth || 600;

      // Calculate scaled dimensions (maintain aspect ratio, max width = container)
      const aspectRatio = img.naturalHeight / img.naturalWidth;
      const displayWidth = Math.min(containerWidth, img.naturalWidth);
      const displayHeight = displayWidth * aspectRatio;

      setImageDimensions({
        width: displayWidth,
        height: displayHeight,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight
      });
      setImageLoaded(true);
    };
    img.src = imageUrl;
  }, [imageUrl]);

  if (!imageUrl) {
    return null;
  }

  if (!imageLoaded) {
    return (
      <Box sx={{ py: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Loading image...
        </Typography>
      </Box>
    );
  }

  // Helper to get box color based on match type
  const getBoxColor = (face) => {
    if (face.match?.type === 'known') return '#4caf50'; // Green
    if (face.match?.type === 'associated') return '#2196f3'; // Blue
    return '#ffc107'; // Yellow/amber for unknown
  };

  // Helper to get label for face
  const getFaceLabel = (face, index) => {
    if (face.match?.type === 'known') {
      return `${face.match.personId} (${face.match.similarity?.toFixed(1)}%)`;
    }
    if (face.match?.type === 'associated') {
      return `Associated (${face.match.similarity?.toFixed(1)}%)`;
    }
    return face.match?.personId || `Unknown #${index + 1}`;
  };

  // Helper to get chip color
  const getChipColor = (face) => {
    if (face.match?.type === 'known') return 'success';
    if (face.match?.type === 'associated') return 'info';
    return 'default';
  };

  return (
    <Box ref={containerRef} sx={{ mt: 2, mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 500 }}>
        Detection Analysis ({detectedFaces.length} {detectedFaces.length === 1 ? 'face' : 'faces'} detected)
      </Typography>

      <Paper
        elevation={2}
        sx={{
          position: 'relative',
          width: imageDimensions.width,
          height: imageDimensions.height,
          overflow: 'hidden',
          borderRadius: 1,
          cursor: 'pointer'
        }}
      >
        {/* Base image */}
        <img
          src={imageUrl}
          alt="Capture with detected faces"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'contain'
          }}
        />

        {/* Bounding boxes overlay */}
        <svg
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            pointerEvents: 'none'
          }}
        >
          {detectedFaces.map((face, index) => {
            // Handle both BoundingBox (from DynamoDB/Rekognition) and boundingBox (normalized)
            const box = face.BoundingBox || face.boundingBox || face.bounding_box;
            if (!box) {
              console.warn(`[DetectionImageCanvas] Face #${index} missing bounding box:`, face);
              return null;
            }

            // Convert normalized coordinates (0-1) to pixel coordinates
            const x = box.Left * imageDimensions.width;
            const y = box.Top * imageDimensions.height;
            const width = box.Width * imageDimensions.width;
            const height = box.Height * imageDimensions.height;

            const color = getBoxColor(face);
            const isHovered = hoveredFaceIndex === index;

            return (
              <g key={index}>
                {/* Bounding box rectangle */}
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill="none"
                  stroke={color}
                  strokeWidth={isHovered ? 3 : 2}
                  opacity={isHovered ? 1 : 0.8}
                  style={{ pointerEvents: 'all', cursor: 'pointer' }}
                  onMouseEnter={() => setHoveredFaceIndex(index)}
                  onMouseLeave={() => setHoveredFaceIndex(null)}
                  onClick={() => onFaceClick?.(face, index)}
                />

                {/* Face index label - positioned outside top-left of box */}
                <text
                  x={x - 5}
                  y={y - 5}
                  fill={color}
                  fontSize="14"
                  fontWeight="bold"
                  style={{ pointerEvents: 'none', textShadow: '0 0 3px black' }}
                >
                  {index + 1}
                </text>
              </g>
            );
          })}
        </svg>

        {/* Hover tooltip */}
        {hoveredFaceIndex !== null && detectedFaces[hoveredFaceIndex] && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: 10,
              right: 10,
              bgcolor: 'rgba(0, 0, 0, 0.85)',
              color: 'white',
              p: 1.5,
              borderRadius: 1,
              pointerEvents: 'none',
              zIndex: 10
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Face #{hoveredFaceIndex + 1}: {getFaceLabel(detectedFaces[hoveredFaceIndex], hoveredFaceIndex)}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Confidence: {(detectedFaces[hoveredFaceIndex].Confidence || detectedFaces[hoveredFaceIndex].confidence || 0).toFixed(1)}%
            </Typography>
            {detectedFaces[hoveredFaceIndex].match?.sightingCount && (
              <Typography variant="caption" sx={{ display: 'block' }}>
                Sightings: {detectedFaces[hoveredFaceIndex].match.sightingCount}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {/* Face legend below image */}
      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {detectedFaces.map((face, index) => (
          <Chip
            key={index}
            label={`#${index + 1} ${getFaceLabel(face, index)}`}
            size="small"
            color={getChipColor(face)}
            sx={{
              fontSize: '0.75rem',
              cursor: 'pointer',
              opacity: hoveredFaceIndex === index ? 1 : 0.85,
              '&:hover': { opacity: 1 }
            }}
            onMouseEnter={() => setHoveredFaceIndex(index)}
            onMouseLeave={() => setHoveredFaceIndex(null)}
            onClick={() => onFaceClick?.(face, index)}
          />
        ))}
      </Box>
    </Box>
  );
}
